/**
 * Triage-level state for cell comments (OR-1534).
 *
 * - Counts: the list response carries each cell's open-comment count; posts,
 *   deletes and resolves made from a popover override it locally so the
 *   badge updates without refetching the page. Overrides are dropped when a
 *   new page of rows arrives (that response already reflects them).
 * - One popover at a time: the open cell is tracked here, keyed by
 *   `submissionId|field`.
 * - The last resolved thread, for the "Thread resolved" message and its Undo.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, setCommentThreadResolved } from "../../api";
import type { TriageRow } from "../../api";
import type { SubmissionCommentThreadWire } from "../../types/submission-comment";

const keyOf = (submissionId: string, field: string) => `${submissionId}|${field}`;

export interface ResolvedThread {
    submissionId: string;
    field: string;
    threadId: string;
}

export interface TriageComments {
    countFor: (row: TriageRow, field: string) => number;
    setCount: (submissionId: string, field: string, count: number) => void;
    isOpen: (submissionId: string, field: string) => boolean;
    setOpen: (submissionId: string, field: string, open: boolean) => void;
    onResolved: (submissionId: string, field: string, thread: SubmissionCommentThreadWire) => void;
}

/** `rowsVersion` changes whenever the list response does. */
export function useTriageComments(rowsVersion: unknown) {
    const [overrides, setOverrides] = useState<Record<string, number>>({});
    const [openKey, setOpenKey] = useState<string | null>(null);
    const [resolved, setResolved] = useState<ResolvedThread | null>(null);
    const [undoing, setUndoing] = useState(false);
    const [undoError, setUndoError] = useState<string | null>(null);

    useEffect(() => {
        setOverrides({});
        setOpenKey(null);
    }, [rowsVersion]);

    const setCount = useCallback((submissionId: string, field: string, count: number) => {
        setOverrides((prev) => {
            const k = keyOf(submissionId, field);
            return prev[k] === count ? prev : { ...prev, [k]: count };
        });
    }, []);

    const comments = useMemo<TriageComments>(
        () => ({
            countFor: (row, field) => overrides[keyOf(row.submissionId, field)] ?? row.commentCounts[field] ?? 0,
            setCount,
            isOpen: (submissionId, field) => openKey === keyOf(submissionId, field),
            setOpen: (submissionId, field, open) => {
                const k = keyOf(submissionId, field);
                setOpenKey((prev) => (open ? k : prev === k ? null : prev));
            },
            onResolved: (submissionId, field, thread) => {
                setOpenKey(null);
                setUndoError(null);
                setResolved({ submissionId, field, threadId: thread.id });
            },
        }),
        [overrides, openKey, setCount],
    );

    const undo = useCallback(async () => {
        if (!resolved) return;
        setUndoing(true);
        setUndoError(null);
        try {
            const reopened = await setCommentThreadResolved(resolved.submissionId, resolved.threadId, false);
            setCount(resolved.submissionId, resolved.field, reopened.num_comments);
            setResolved(null);
        } catch (err) {
            // e.g. 409 "A newer thread is open on this field." — the backend's copy.
            setUndoError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
            setResolved(null);
        } finally {
            setUndoing(false);
        }
    }, [resolved, setCount]);

    return {
        comments,
        resolved,
        undoing,
        undo,
        dismissResolved: () => setResolved(null),
        undoError,
        dismissUndoError: () => setUndoError(null),
    };
}
